import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { RoomRateTab } from '@/components/pages/setting/room-rate/tab'
import { settingHeaderContainerStyle } from '@/components/pages/setting/guest-room/room/layout'
import { listContainerStyle } from '@/constants/layout'
import { Button } from '@/components/atoms/button'
import { Select, Option } from '@/components/atoms/select'
import { Popup } from '@/components/atoms/popup'
import { UnsavedChangesRouteGuard } from '@/components/molecules/unsaved-changes-route-guard'
import { fetchBaseRates, updateBaseRates } from '@/apis/aipass'
import { usePriceRanks } from '@/hooks/use-price-ranks'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { isStaffGuardCancelledError } from '@/libs/errors'
import { LoaderContextCreator } from '@/contexts/loader'
import {
  GUEST_COUNT_OPTIONS,
  loadBaseRateViewPrefs,
  resolveAdoptedPriceType,
  saveBaseRateViewPrefs,
  toAmountMap,
  toApiSheetChargeType,
  toApiPaxBand,
  toPricePayload,
  toRoomTypes,
} from '@/models/room-rate/base-rate'
import type { BaseRateAmountMap, BaseRateGuestCount, BaseRatePriceType, BaseRateRoomType, BaseRateRow } from '@/models/room-rate/base-rate'
import { formatRoomRateAmount } from '@/models/room-rate/format'
import { getPriceRankColorHex, resolvePriceRankCellTint } from '@/models/room-rate/price-rank'
import type { PriceRank } from '@/models/room-rate/price-rank'
import { roomRatePillSelectStyle } from '@/components/pages/setting/room-rate/shared-styles'

type EditingCell = {
  roomId: string
  rankId: string
}

const parseAmountInput = (raw: string): number | null => {
  const normalized = raw.normalize('NFKC').replace(/[^\d]/g, '')
  return normalized === '' ? null : Number(normalized)
}

export const SettingRoomRateBase: React.FC = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()
  const { ranks, loadPriceRanks } = usePriceRanks()
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [roomTypes, setRoomTypes] = useState<BaseRateRoomType[]>([])
  const [priceType, setPriceType] = useState<BaseRatePriceType>('rc')
  const [guestCount, setGuestCount] = useState<BaseRateGuestCount>(1)
  // 施設の採用区分（保存済み）。表示中シートと差があるときだけ Footer で確定する
  const [adoptedPriceType, setAdoptedPriceType] = useState<BaseRatePriceType>('rc')
  const [isPriceTypePopupOpen, setIsPriceTypePopupOpen] = useState(false)
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
  const [draftValue, setDraftValue] = useState('')
  const [amounts, setAmounts] = useState<BaseRateAmountMap>({})
  const sheetRequestId = useRef(0)
  const commitInFlightRef = useRef(false)
  const amountsRef = useRef(amounts)
  const roomTypesRef = useRef(roomTypes)
  const ranksRef = useRef(ranks)
  const priceTypeRef = useRef(priceType)
  const guestCountRef = useRef(guestCount)
  const adoptedPriceTypeRef = useRef(adoptedPriceType)

  amountsRef.current = amounts
  roomTypesRef.current = roomTypes
  ranksRef.current = ranks
  priceTypeRef.current = priceType
  guestCountRef.current = guestCount
  adoptedPriceTypeRef.current = adoptedPriceType

  const isAdoptionPending = priceType !== adoptedPriceType

  const applyRows = (rows: BaseRateRow[], nextRanks: PriceRank[]) => {
    setRoomTypes(toRoomTypes(rows))
    setAmounts(toAmountMap(rows, nextRanks))
  }

  const fetchSheet = (type: BaseRatePriceType, guest: BaseRateGuestCount) => {
    const sheetChargeType = toApiSheetChargeType(type)
    return fetchBaseRates(
      sheetChargeType === 'rc' ? { sheetChargeType: 'rc' } : { sheetChargeType: 'per_person', paxBand: toApiPaxBand(guest) },
    )
  }

  const loadSheet = async (type: BaseRatePriceType, guest: BaseRateGuestCount, nextRanks = ranksRef.current) => {
    const requestId = ++sheetRequestId.current
    const rows = (await fetchSheet(type, guest)) || []
    if (requestId !== sheetRequestId.current) return undefined
    applyRows(rows, nextRanks)
    setPriceType(type)
    setGuestCount(guest)
    saveBaseRateViewPrefs({ priceType: type, guestCount: guest })
    return rows
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsPageLoading(true)
      const requestId = ++sheetRequestId.current
      try {
        // 採用区分は rc シートから取得。表示シートは前回見た区分を優先
        const [nextRanks, rcRows] = await Promise.all([loadPriceRanks(), fetchSheet('rc', 1)])
        if (cancelled || requestId !== sheetRequestId.current) return
        const adopted = resolveAdoptedPriceType(rcRows || [])
        setAdoptedPriceType(adopted)
        const viewPrefs = loadBaseRateViewPrefs()
        const initialType = viewPrefs?.priceType ?? adopted
        const initialGuest = viewPrefs?.guestCount ?? 1
        if (initialType === 'perPerson') {
          const perPersonRows = (await fetchSheet('perPerson', initialGuest)) || []
          if (cancelled || requestId !== sheetRequestId.current) return
          applyRows(perPersonRows, nextRanks)
          setPriceType('perPerson')
          setGuestCount(initialGuest)
          saveBaseRateViewPrefs({ priceType: 'perPerson', guestCount: initialGuest })
        } else {
          applyRows(rcRows || [], nextRanks)
          setPriceType('rc')
          setGuestCount(initialGuest)
          saveBaseRateViewPrefs({ priceType: 'rc', guestCount: initialGuest })
        }
      } catch (e) {
        if (!cancelled) {
          errorHandler(e)
        }
      } finally {
        if (!cancelled) {
          setIsPageLoading(false)
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const changeSheet = async (nextType: BaseRatePriceType, nextGuest: BaseRateGuestCount) => {
    if (nextType === priceType && nextGuest === guestCount) {
      setIsPriceTypePopupOpen(false)
      return
    }
    // 採用区分の切替は編集権限が必要
    if (nextType !== priceType && !guardSettingsEdit()) {
      setIsPriceTypePopupOpen(false)
      return
    }
    try {
      setIsLoading(true)
      setEditingCell(null)
      await loadSheet(nextType, nextGuest)
      setIsPriceTypePopupOpen(false)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const onClickAdoptionCancel = async () => {
    try {
      setIsLoading(true)
      setEditingCell(null)
      setIsPriceTypePopupOpen(false)
      await loadSheet(adoptedPriceTypeRef.current, guestCountRef.current)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const onClickAdoptionSave = async () => {
    if (!guardSettingsEdit()) return
    const currentPriceType = priceTypeRef.current
    const currentGuestCount = guestCountRef.current
    const sheetChargeType = toApiSheetChargeType(currentPriceType)
    try {
      setIsLoading(true)
      setEditingCell(null)
      await updateBaseRates({
        sheetChargeType,
        ...(sheetChargeType === 'per_person' ? { paxBand: toApiPaxBand(currentGuestCount) } : {}),
        prices: toPricePayload(amountsRef.current, roomTypesRef.current, ranksRef.current),
        adoptedChargeType: sheetChargeType,
      })
      const rows = await loadSheet(currentPriceType, currentGuestCount)
      setAdoptedPriceType(rows ? resolveAdoptedPriceType(rows) : currentPriceType)
    } catch (e) {
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const startCellEdit = (roomId: string, rankId: string) => {
    if (!guardSettingsEdit() || commitInFlightRef.current) return
    const current = amountsRef.current[roomId]?.[rankId] ?? null
    setDraftValue(current === null ? '' : String(current))
    setEditingCell({ roomId, rankId })
    setIsPriceTypePopupOpen(false)
  }

  /** レストラン管理と同様、編集終了（blur / Enter）で差分があれば即保存 */
  const commitCell = async () => {
    if (!editingCell || commitInFlightRef.current) return
    commitInFlightRef.current = true

    const { roomId, rankId } = editingCell
    const nextValue = parseAmountInput(draftValue)
    const prevValue = amountsRef.current[roomId]?.[rankId] ?? null

    setEditingCell(null)

    if (nextValue === prevValue) {
      commitInFlightRef.current = false
      return
    }
    if (!guardSettingsEdit()) {
      commitInFlightRef.current = false
      return
    }

    const previousAmounts = amountsRef.current
    const nextAmounts: BaseRateAmountMap = {
      ...previousAmounts,
      [roomId]: {
        ...previousAmounts[roomId],
        [rankId]: nextValue,
      },
    }
    const currentPriceType = priceTypeRef.current
    const currentGuestCount = guestCountRef.current
    const sheetChargeType = toApiSheetChargeType(currentPriceType)
    const adoptionPending = currentPriceType !== adoptedPriceTypeRef.current

    setAmounts(nextAmounts)
    try {
      setIsLoading(true)
      // 採用区分未確定の切替中は価格のみ保存（採用は Footer 保存で確定）
      await updateBaseRates({
        sheetChargeType,
        ...(sheetChargeType === 'per_person' ? { paxBand: toApiPaxBand(currentGuestCount) } : {}),
        prices: toPricePayload(nextAmounts, roomTypesRef.current, ranksRef.current),
        ...(adoptionPending ? {} : { adoptedChargeType: sheetChargeType }),
      })
      await loadSheet(currentPriceType, currentGuestCount)
    } catch (e) {
      try {
        await loadSheet(currentPriceType, currentGuestCount)
      } catch (reloadError) {
        setAmounts(previousAmounts)
        if (!isStaffGuardCancelledError(reloadError)) {
          errorHandler(reloadError)
        }
      }
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      commitInFlightRef.current = false
      setIsLoading(false)
    }
  }

  const priceTypeLabel = useMemo(() => {
    return priceType === 'rc' ? t('baseRate.priceType.rc') : t('baseRate.priceType.perPerson')
  }, [priceType, t])

  return (
    <SettingsLayout loading={isPageLoading}>
      <>
        <UnsavedChangesRouteGuard hasUnsaved={isAdoptionPending} unsavedLabels={[{ key: 'baseRate', label: t('Base rate') }]} />
        <div css={settingHeaderContainerStyle}>
          <RoomRateTab currentTab="baseRate" />
        </div>
        <div css={[listContainerStyle, pageBodyStyle]}>
          <div css={toolbarStyle}>
            <div css={toolbarLeftStyle}>
              <span css={priceTypeLabelStyle}>{t('baseRate.priceTypeLabel')} : </span>
              <div css={priceTypeViewStyle}>
                <span css={priceTypeValueStyle}>{priceTypeLabel}</span>
                {!isAdoptionPending && (
                  <div css={changeButtonWrapStyle}>
                    <Button buttonType={3} width={56} height={32} fontSize={12} onClick={() => setIsPriceTypePopupOpen(prev => !prev)}>
                      {t('Change')}
                    </Button>
                    <Popup isOpen={isPriceTypePopupOpen} onClose={() => setIsPriceTypePopupOpen(false)} customStyle={priceTypePopupStyle}>
                      <ul>
                        <li css={popupItemStyle} onClick={() => changeSheet('rc', guestCount)}>
                          {t('baseRate.priceType.rc')}
                        </li>
                        <li css={popupItemStyle} onClick={() => changeSheet('perPerson', guestCount)}>
                          {t('baseRate.priceType.perPerson')}
                        </li>
                      </ul>
                    </Popup>
                  </div>
                )}
              </div>

              {!isAdoptionPending && priceType === 'perPerson' && (
                <div css={guestCountWrapStyle}>
                  <span css={priceTypeLabelStyle}>{t('baseRate.guestCountLabel')} : </span>
                  <Select
                    value={String(guestCount)}
                    onChange={v => changeSheet('perPerson', Number(v) as BaseRateGuestCount)}
                    customStyle={guestSelectOverrideStyle}
                  >
                    {GUEST_COUNT_OPTIONS.map(count => (
                      <Option key={count} value={String(count)}>
                        {count === 10 ? t('baseRate.guestCount.10plus') : t('baseRate.guestCount.n', { count })}
                      </Option>
                    ))}
                  </Select>
                </div>
              )}

              {isAdoptionPending && (
                <div css={adoptionActionsStyle}>
                  <Button
                    buttonType={3}
                    width={92}
                    height={32}
                    fontSize={12}
                    borderRadius={40}
                    onClick={() => void onClickAdoptionCancel()}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button buttonType={1} width={68} height={32} fontSize={12} borderRadius={40} onClick={() => void onClickAdoptionSave()}>
                    {t('Save')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div css={tableScrollStyle}>
            <table css={tableStyle} style={{ minWidth: 149 + 107 * Math.max(ranks.length, 1) }}>
              <thead>
                <tr>
                  <th css={[headerCellStyle, roomTypeHeaderStyle]}>{t('Room type')}</th>
                  {ranks.map(rank => (
                    <th key={rank.id} css={[headerCellStyle, rankHeaderStyle]} style={{ background: getPriceRankColorHex(rank.color) }}>
                      {rank.shortName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roomTypes.map(room => (
                  <tr key={room.id}>
                    <td css={[bodyCellStyle, roomTypeCellStyle]}>
                      <span css={roomTypeNameStyle}>{room.name}</span>
                    </td>
                    {ranks.map(rank => {
                      const amount = amounts[room.id]?.[rank.id] ?? null
                      const isActive = editingCell?.roomId === room.id && editingCell?.rankId === rank.id
                      const tint = resolvePriceRankCellTint(rank.color)
                      return (
                        <td
                          key={rank.id}
                          css={[bodyCellStyle, amountCellStyle]}
                          style={{ background: tint, cursor: isSettingsReadOnly ? 'default' : 'pointer' }}
                          onClick={() => {
                            if (isActive) return
                            startCellEdit(room.id, rank.id)
                          }}
                        >
                          {isActive ? (
                            <input
                              css={cellInputStyle}
                              autoFocus
                              value={draftValue}
                              onChange={e => setDraftValue(e.target.value)}
                              onBlur={() => {
                                void commitCell()
                              }}
                              onKeyDown={e => {
                                if (e.key !== 'Enter') return
                                if (e.nativeEvent.isComposing) return
                                e.preventDefault()
                                void commitCell()
                              }}
                            />
                          ) : (
                            <span css={amountTextStyle}>{formatRoomRateAmount(amount)}</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    </SettingsLayout>
  )
}

const pageBodyStyle = css({
  // 表はカード端まで伸ばす。ツールバーだけ左右余白を持つ
  padding: '24px 0 32px',
  background: '#fff',
  minHeight: 600,
})

const toolbarStyle = css({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 40,
  minHeight: 32,
  padding: '0 32px',
})

const toolbarLeftStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
})

const priceTypeViewStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
})

const priceTypeLabelStyle = css({
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
})

const priceTypeValueStyle = css({
  color: '#676767',
  fontSize: 14,
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
})

const changeButtonWrapStyle = css({
  position: 'relative',
})

const priceTypePopupStyle = css({
  width: 140,
  left: 0,
  top: 36,
})

const popupItemStyle = css({
  listStyle: 'none',
  padding: '10px 16px',
  fontSize: 14,
  color: '#272727',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
})

const guestCountWrapStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

// Figma: 料金区分操作の右にキャンセル(92x32) / 保存
const adoptionActionsStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 8,
})

const guestSelectOverrideStyle = css(roomRatePillSelectStyle, {
  width: 128,
  '> div': {
    borderColor: '#F2A40B !important',
  },
})

const tableScrollStyle = css({
  overflowX: 'auto',
  margin: '0',
})

const tableStyle = css({
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
})

const headerCellStyle = css({
  height: 31,
  padding: '0 15px 0 14px',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  border: '1px solid #F2F2F2',
  boxSizing: 'border-box',
  verticalAlign: 'middle',
  lineHeight: '29px',
})

const roomTypeHeaderStyle = css({
  background: '#F2F2F2',
  color: '#676767',
  width: 149,
})

const rankHeaderStyle = css({
  color: '#fff',
})

const bodyCellStyle = css({
  // 2 行（20px × 2）＋上下余白 8px ＋枠線が入る高さ。1 行でも 2 行でも同じ
  height: 58,
  border: '1px solid #F2F2F2',
  fontSize: 14,
  fontWeight: 'bold',
  color: '#272727',
  boxSizing: 'border-box',
  verticalAlign: 'middle',
})

const roomTypeCellStyle = css({
  background: '#fff',
  textAlign: 'left',
  width: 149,
  // 金額セルと同じ上下 8px にして、行高 58px のまま 2 行を収める
  padding: '8px 14px',
  cursor: 'default',
})

const roomTypeNameStyle = css({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
  lineHeight: '20px',
  maxHeight: 40,
})

const amountCellStyle = css({
  padding: '8px 8px 8px 14px',
  textAlign: 'left',
  cursor: 'pointer',
})

const amountTextStyle = css({
  display: 'inline-block',
  minWidth: 61,
  fontSize: 14,
  fontWeight: 'bold',
  color: '#272727',
  lineHeight: '21px',
})

const cellInputStyle = css({
  width: '100%',
  height: 28,
  boxSizing: 'border-box',
  border: '1px solid #CCCCCC',
  borderRadius: 20,
  background: '#fff',
  textAlign: 'left',
  padding: '4px 8px',
  fontSize: 14,
  fontWeight: 500,
  color: '#272727',
  outline: 'none',
  lineHeight: 'normal',
})
